<script lang="ts">
	import type { Mediator, SvelteAsr, i18n } from 'types/common'
	import type { InterchangeScreenManufacturers$result, InterchangeScreenModels$result } from '$houdini'
	import type { InventoryType } from './interchange'

	import { graphql } from '$houdini'
	import Card from '@isoftdata/svelte-card'
	import Icon from '@isoftdata/svelte-icon'
	import Input from '@isoftdata/svelte-input'
	import Button from '@isoftdata/svelte-button'
	import YearInput from '@isoftdata/svelte-year-input'

	import Autocomplete from 'components/AutocompleteWithOnMount.svelte'
	import VinLookupModal from 'components/VinLookupModal.svelte'
	import type { InterchangeRecord } from 'utility/interchange-lookup'

	import { getContext, onMount } from 'svelte'

	const manufacturerListQuery = graphql(`
		query InterchangeScreenManufacturers($filter: ManufacturerFilter) {
			manufacturers(filter: $filter) {
				id
				name
			}
		}
	`)

	const modelListQuery = graphql(`
		query InterchangeScreenModels($filter: ModelFilter) {
			models(filter: $filter) {
				id
				name
			}
		}
	`)

	type Manufacturer = InterchangeScreenManufacturers$result['manufacturers'][number]
	type Model = InterchangeScreenModels$result['models'][number]

	interface Props {
		asr: SvelteAsr
		inventoryTypeList: Array<InventoryType>
		inventoryType: InventoryType | null
		year: number | null
		initialManufacturer: string | null
		initialModel: string | null
		interchangeNumber: string
		subInterchangeNumber: string
		lastSearchType: 'VEHICLE_INFO' | 'INTERCHANGE_NUMBER' | null
	}

	let {
		asr,
		inventoryTypeList,
		inventoryType,
		year,
		initialManufacturer,
		initialModel,
		interchangeNumber,
		subInterchangeNumber,
		lastSearchType,
	}: Props = $props()

	let manufacturerList: Array<Manufacturer> = $state([])
	let modelList: Array<Model> = $state([])
	let isLoadingManufacturers: boolean = $state(false)
	let isLoadingModels: boolean = $state(false)
	let manufacturer: Manufacturer | null = $state<Manufacturer | null>(null)
	let model: Model | null = $state<Model | null>(null)
	let vinLookupModal: VinLookupModal | undefined = $state()

	const mediator = getContext<Mediator>('mediator')
	const { t: translate } = getContext<i18n>('i18next')

	async function loadManufacturers() {
		isLoadingManufacturers = true
		try {
			const res = await manufacturerListQuery.fetch({
				variables: {
					filter: {
						inventoryTypeId: inventoryType?.id ? [inventoryType?.id] : null,
					},
				},
			})
			manufacturerList = res.data?.manufacturers || []
		} catch (err) {
			mediator.publish('showMessage', {
				heading: translate('interchange.errorLoadingManufacturers', 'Error loading manufacturers'),
				message:
					err instanceof Error
						? err.message
						: translate(
								'interchange.anErrorOccurredWhileLoadingManufacturers',
								'An error occurred while loading manufacturers.',
							),
				type: 'warning',
				time: 10000,
			})
		} finally {
			isLoadingManufacturers = false
		}
	}

	async function loadModels(setValue?: string) {
		if (!inventoryType && !manufacturer?.id) {
			modelList = []
			return
		}

		isLoadingModels = true
		try {
			const res = await modelListQuery.fetch({
				variables: {
					filter: {
						inventoryTypeId: inventoryType?.id || null,
						manufacturerId: manufacturer?.id ? manufacturer.id : null,
					},
				},
			})
			modelList = res.data?.models || []
			if (setValue) {
				const setValueUpper = setValue.toUpperCase()
				model = modelList.find(m => m.name.toUpperCase() === setValueUpper) ?? null
			}
		} catch (err) {
			mediator.publish('showMessage', {
				heading: translate('interchange.errorLoadingModels', 'Error loading models'),
				message:
					err instanceof Error
						? err.message
						: translate('interchange.anErrorOccurredWhileLoadingModels', 'An error occurred while loading models.'),
				type: 'warning',
				time: 5000,
			})
		} finally {
			isLoadingModels = false
		}
	}

	async function search(type: 'VEHICLE_INFO' | 'INTERCHANGE_NUMBER') {
		if (inventoryType?.id) {
			asr.go('app.interchange.results', {
				lastSearchType: type,
				inventoryTypeId: inventoryType?.id || undefined,
				year: year ? year : undefined,
				manufacturer: manufacturer?.name || undefined,
				model: model?.name || undefined,
				interchangeNumber: interchangeNumber || undefined,
				subInterchangeNumber: subInterchangeNumber || undefined,
			})
		}
	}

	function focusInventoryType() {
		const inventoryTypeInput = document.getElementById('inventoryType')
		inventoryTypeInput?.focus()
	}

	async function attemptToSetVehicleInfoSearchFields(vehicleInfo: {
		year: number | null
		make: string
		model: string
	}) {
		year = vehicleInfo.year
		const decodedManufacturer = vehicleInfo.make.toUpperCase()
		const matchingManufacturer = manufacturerList.find(m => m.name.toUpperCase() === decodedManufacturer)
		if (matchingManufacturer) {
			manufacturer = matchingManufacturer
			await loadModels()
			const decodedModel = vehicleInfo?.model.toUpperCase()
			model = modelList.find(m => m.name.toUpperCase() === decodedModel) ?? null
		} else {
			manufacturer = null
			model = null
		}
	}

	onMount(async () => {
		focusInventoryType()
	})
</script>

<div class="card">
	<div class="card-header d-flex">
		<div class="mr-auto">
			<h4>
				<Icon
					icon="random"
					class="mr-1"
				/><span class="d-none d-sm-inline-block">{translate('common:interchange', 'Interchange')}</span>
			</h4>
		</div>
		<div>
			<Button
				outline
				size="sm"
				onclick={() => vinLookupModal?.open('')}>{translate('interchange.VINLookup', 'VIN Lookup')}</Button
			>
		</div>
	</div>
	<div class="card-body">
		<Card
			collapsible
			class="mb-3"
		>
			{#snippet header()}
				<div class="d-flex align-items-end">
					<h4 class="mb-0 mr-2">{translate('common:filters', 'Filters')}</h4>
					<Button
						size="xs"
						icon="broom"
						outline
						color="danger"
						href={asr.makePath(null, { refresh: Date.now() })}
					/>
				</div>
			{/snippet}
			<div class="row">
				<div class="col-12 col-lg-3 col-md-6 col-sm-8">
					<Autocomplete
						id="inventoryType"
						label={translate('common:inventoryType', 'Inventory Type')}
						options={inventoryTypeList}
						required
						getLabel={inventoryType => (inventoryType ? `${inventoryType.name} - ${inventoryType.id}` : '')}
						bind:value={inventoryType}
						emptyValue={null}
						change={() => loadManufacturers()}
					/>
				</div>
			</div>
			<div class="row">
				<div class="col-lg-8 mb-3 mb-lg-0">
					<form
						onsubmit={e => {
							e.preventDefault()
							search('VEHICLE_INFO')
						}}
					>
						<div
							class="card border"
							class:border-primary={lastSearchType === 'VEHICLE_INFO'}
						>
							<h6 class="card-header">{translate('common:vehicleInfo', 'Vehicle Info')}</h6>
							<div class="card-body">
								<div class="row align-items-end">
									<div class="col-md-5 col-lg-2">
										<YearInput
											label={translate('common:year', 'Year')}
											bind:value={year}
										/>
									</div>
									<div class="col-md-7 col-lg-4">
										<Autocomplete
											label={translate('common:manufacturerMake', 'Manufacturer / Make')}
											options={manufacturerList}
											getLabel={manufacturer => manufacturer?.name ?? ''}
											bind:value={manufacturer}
											emptyValue={null}
											change={() => {
												loadModels()
												model = null
											}}
											isLoading={isLoadingManufacturers}
											onMount={async () => {
												await loadManufacturers()
												if (initialManufacturer) {
													const manufacturerUpper = initialManufacturer.toUpperCase()
													manufacturer = manufacturerList.find(m => m.name.toUpperCase() === manufacturerUpper) ?? null
													if (initialModel) {
														await loadModels(initialModel)
													}
												}
											}}
										/>
									</div>
									<div class="col-md-7 col-lg-4">
										<Autocomplete
											label={translate('common:model', 'Model')}
											options={modelList}
											getLabel={model => model?.name ?? ''}
											bind:value={model}
											emptyValue={null}
											isLoading={isLoadingModels}
										/>
									</div>
								</div>
							</div>
							<div
								class="card-footer d-flex flex-wrap justify-content-between"
								style="gap: 0.25rem"
							>
								<Button
									outline
									size="sm"
									disabled={!(inventoryType && year && manufacturer)}
									icon="magnifying-glass"
									colorGreyDisabled={false}
									href={asr.makePath('app.vehicle-search.results', {
										YEAR_RANGE: JSON.stringify({ from: year, to: year }),
										VEHICLE_MANUFACTURER: manufacturer?.id,
										VEHICLE_MODEL: model?.id,
									})}
									title={translate(
										'interchange.searchesForVehiclesMatchCriteria',
										'Searches for vehicles matching the current criteria on the vehicle screen',
									)}>{translate('common:searchVehicles', 'Search Vehicles')}</Button
								>
								<Button
									size="sm"
									icon="random"
									disabled={!inventoryType}
									colorGreyDisabled={false}
									type="submit">{translate('common:interchangeSearch', 'Interchange Search')}</Button
								>
							</div>
						</div>
					</form>
				</div>
				<div class="col-lg-4">
					<form
						onsubmit={e => {
							e.preventDefault()
							search('INTERCHANGE_NUMBER')
						}}
					>
						<div
							class="card border"
							class:border-primary={lastSearchType === 'INTERCHANGE_NUMBER'}
						>
							<h6 class="card-header">{translate('common:interchangeNum', 'Interchange #')}</h6>
							<div class="card-body">
								<div class="row align-items-end">
									<div class="col-md-9 col-lg-6 col-xl-7">
										<Input
											label={translate('common:interchangeNum', 'Interchange #')}
											bind:value={interchangeNumber}
											placeholder="(eg. 123456)"
										/>
									</div>
									<div class="col-md-3 col-lg-6 col-xl-5">
										<Input
											label={translate('common:subInterchange', 'Sub Interchange')}
											bind:value={subInterchangeNumber}
											placeholder="(eg. A)"
										/>
									</div>
								</div>
							</div>
							<div class="card-footer d-flex justify-content-end">
								<Button
									size="sm"
									icon="random"
									disabled={!inventoryType}
									colorGreyDisabled={false}
									type="submit">{translate('common:interchangeNumSearch', 'Interchange # Search')}</Button
								>
							</div>
						</div>
					</form>
				</div>
			</div>
		</Card>
		<uiView></uiView>
	</div>
</div>

<VinLookupModal
	bind:this={vinLookupModal}
	confirmButtonText={translate('interchange.useVinInfo', 'Use VIN Info')}
	confirm={async decodedVIN => attemptToSetVehicleInfoSearchFields(decodedVIN)}
/>
